import {
  Component,
  HostBinding,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ItemView,
  ItemViewProviderDirective,
  log,
  NtapCommonComponent,
  NtapGlobalConstants,
  UIComponents,
  Util,
  View,
  ViewData,
  ViewState
} from '@ntap/core';
import {
  combineLatest,
  first,
  mergeMap,
  Observable,
  of,
  skip,
  tap
} from 'rxjs';

import { DynamicFormService } from '@ntap/common';
import {
  ImagePanelItemProviderService,
  ParentItemProviderService
} from '@app/shared';
import {
  ObrazacUnosaDTO,
  ObrazacUnosaService
} from '../fast-data-entry/service/obrazac-unosa.service';

@Component({
  selector: 'app-jedinica-gradiva',
  templateUrl: './jedinica-gradiva.component.html',
  styleUrls: ['./jedinica-gradiva.component.scss'],
  providers: [
    DynamicFormService,
    { provide: ObrazacUnosaService, useClass: ObrazacUnosaService },
    ParentItemProviderService,
    ImagePanelItemProviderService
  ]
})
export class JedinicaGradivaComponent
  extends NtapCommonComponent
  implements OnInit, OnDestroy
{
  INIT_COMP_ID = UIComponents.LISTVIEW;
  currentDosjeListViewName: string;
  currentDosjeListViewState: object | null = null;
  currentDosjeListViewStateFilter: object = {};

  parentItem: any;
  viewNameClass: string;

  jedinicaGradivaId: number;
  view$: Observable<View>;
  viewData$: Observable<ViewData>;
  entryTemplateDisabled = false;

  ITEMVIEW_UI_COMPID = UIComponents.ITEMVIEW;
  rokCuvanjaViewName: string;
  rokCuvanjaViewState: ViewState | null = null;

  private fastDataEntryService = inject(ObrazacUnosaService);

  @HostBinding('class') get class() {
    return this.viewNameClass;
  }

  constructor(
    protected route: ActivatedRoute,
    protected parentItemProvider: ParentItemProviderService,
    protected itemViewProvider: ItemViewProviderDirective<
      ItemView & { $entryTemplateId: number }
    >
  ) {
    super();
    this.addSubscription(
      this.itemViewProvider.view$
        .pipe(
          first(),
          mergeMap((view: any) => {
            this.viewNameClass = view.name;
            this.entryTemplateDisabled = view.name.indexOf('readonly') > 0;
            if (!this.entryTemplateDisabled) {
              return this.fastDataEntryService
                .selectEntryTemplateById(view.$entryTemplateId)
                .pipe(
                  tap(() => {
                    this.addSubscription(
                      this.fastDataEntryService.selectEntryTemplate$
                        .pipe(
                          skip(1), // skipamo inicijalno postavljanje, a slusamo na daljnje promjene
                          tap((e) => {
                            this.changeObrazacUpisaJedinice(e);
                          })
                        )
                        .subscribe()
                    );
                  })
                );
            } else {
              return of({});
            }
          })
        )
        .subscribe()
    );

    this.addSubscription(
      this.itemViewProvider.item$
        .pipe(
          tap((item: any) => {
            const jedinicaGradivaId = item['en~jedinica_gradiva~id'];
            this.rokCuvanjaViewName = 'rok_cuvanja_item';
            this.rokCuvanjaViewState = {
              'sv~rok_cuvanja~entitet_id': jedinicaGradivaId,
              $parentId: jedinicaGradivaId,
              $parentType: 'en~jedinica_gradiva',
              $readonly: this.isRootViewReadonly()
            };
          })
        )
        .subscribe()
    );
  }

  private isRootViewReadonly() {
    return this.itemViewProvider.viewName === 'jedinica_gradiva_item_readonly';
  }

  removeButtons(activeTab: number) {
    const toolbar: HTMLDivElement | null = document.querySelector(
      'app-jedinica-gradiva > ntap-prime-common-itemview > p-footer > p-toolbar > div[role="toolbar"]'
    );
    if (!toolbar) {
      return;
    }
    if (activeTab === 1) {
      toolbar!.style!.display = 'none';
    } else {
      toolbar!.style!.display = 'block';
    }
  }

  ngOnInit() {
    this.subscribeToRouteChange();
  }

  subscribeToRouteChange() {
    log('subscribe to route params');
    this.addSubscription(
      combineLatest([
        this.route.paramMap,
        this.parentItemProvider.parentItem$
      ]).subscribe(([, parentItem]: [params: any, parentItem: any]) => {
        log('param change');
        this.parentItem = parentItem;
        this.changeView('pojavni_oblici', parentItem);
      })
    );
  }

  changeView(viewName: string, parentItem: any) {
    log(`CHANGE NEW view: ${viewName}`);
    this.currentDosjeListViewName = viewName;
    this.currentDosjeListViewState = {
      [Util.getFullAttrName('en.pojavni_oblik', 'jedinica_gradiva_id')]:
        parentItem[
          Util.getFullAttrName(
            parentItem[NtapGlobalConstants.ATTR_$mainType],
            NtapGlobalConstants.ATTR_id
          )
        ],
      $readonly: this.isRootViewReadonly()
    };
    this.currentDosjeListViewStateFilter = {};
  }

  changeObrazacUpisaJedinice(entryTemplate: ObrazacUnosaDTO) {
    this.itemViewProvider.item$
      .pipe(
        first(),
        tap((item: any) => {
          const mainIdAttr = Util.getFullAttrName(
            item[NtapGlobalConstants.ATTR_$itemType],
            NtapGlobalConstants.ATTR_id
          );
          const currentId = item[mainIdAttr];
          this.itemViewProvider.viewName = entryTemplate.name;
          this.itemViewProvider.viewState = Object.assign(
            {},
            this.itemViewProvider.viewState,
            { [mainIdAttr]: currentId }
          );
        })
      )
      .subscribe();
  }
}
